import { GATEWAY_BASE_URL } from '../../config/endpoints.ts';
import { useCatalog } from '../../hooks/useCatalog.ts';

function Code({ children }: { readonly children: string }): JSX.Element {
  return (
    <pre className="my-4 overflow-x-auto rounded-xl bg-ink p-5 font-mono text-[13px] leading-relaxed text-linen">
      <code>{children}</code>
    </pre>
  );
}

export function ApiReference(): JSX.Element {
  const catalog = useCatalog();
  const models = catalog.data?.models ?? [];

  return (
    <article>
      <h1 className="text-[28px] font-semibold text-ink mb-2">API 说明</h1>
      <p className="text-ink-muted mb-10">
        哪都通网关完全兼容 Anthropic 官方 API 协议。只需替换 Base URL，其他一切保持不变。
      </p>

      <section className="mb-10">
        <h2 className="text-[20px] font-semibold text-ink mb-4">接入端点</h2>
        <Code>{GATEWAY_BASE_URL}</Code>
        <p className="text-ink-muted">
          所有 Anthropic API 路径均可直接使用，包括 <code className="bg-butter px-1.5 py-0.5 rounded text-[13px]">/v1/messages</code> 和 <code className="bg-butter px-1.5 py-0.5 rounded text-[13px]">/v1/models</code>。
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-[20px] font-semibold text-ink mb-4">认证方式</h2>
        <p className="text-ink-muted mb-3">支持两种认证方式，与 Anthropic 官方一致：</p>
        <Code>{`# 方式一：x-api-key header（推荐）
x-api-key: YOUR_API_KEY

# 方式二：Bearer token
Authorization: Bearer YOUR_API_KEY`}</Code>
      </section>

      <section className="mb-10">
        <h2 className="text-[20px] font-semibold text-ink mb-4">协议兼容性</h2>
        <div className="rounded-xl bg-butter/40 p-5 text-[14px]">
          <ul className="space-y-2 text-ink-muted">
            <li className="flex items-start gap-2">
              <span className="text-claude mt-0.5">✓</span>
              <span><strong>Anthropic /v1/messages</strong> — 完全兼容，包括 streaming（SSE）和 non-streaming 模式</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-claude mt-0.5">✓</span>
              <span><strong>Tool Use</strong> — 支持 function calling，请求和响应格式与官方一致</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-claude mt-0.5">✓</span>
              <span><strong>Vision</strong> — 支持图片输入，Base64 和 URL 两种方式</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-claude mt-0.5">✓</span>
              <span><strong>Streaming</strong> — SSE 格式，实时输出 token，延迟体验与直连一致</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-claude mt-0.5">✓</span>
              <span><strong>错误格式</strong> — 错误响应保持 Anthropic 原始格式，客户端无需额外适配</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-[20px] font-semibold text-ink mb-4">请求示例</h2>
        <Code>{`curl ${GATEWAY_BASE_URL}/v1/messages \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "content-type: application/json" \\
  -H "anthropic-version: 2023-06-01" \\
  -d '{
    "model": "claude-sonnet-4-6",
    "max_tokens": 1024,
    "messages": [
      {"role": "user", "content": "用 Python 写一个快速排序"}
    ]
  }'`}</Code>
        <p className="text-ink-muted text-[13px]">
          流式请求只需在 body 中加入 <code className="bg-butter px-1.5 py-0.5 rounded text-[13px]">{'"stream": true'}</code>，响应为 SSE 格式。
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-[20px] font-semibold text-ink mb-4">支持的模型</h2>
        {models.length === 0 ? (
          <div className="h-20 animate-pulse rounded-xl bg-butter/50" />
        ) : (
          <div className="overflow-hidden rounded-xl bg-butter/30">
            <table className="w-full text-left text-[14px]">
              <thead>
                <tr className="text-[11px] font-mono uppercase tracking-wider text-ink-subtle">
                  <th className="px-5 py-3 font-medium">厂商</th>
                  <th className="px-5 py-3 font-medium">模型 ID</th>
                  <th className="px-5 py-3 font-medium">名称</th>
                  <th className="px-5 py-3 text-right font-medium">上下文窗口</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={`${m.vendor}/${m.model}`} className="border-t border-ink/5">
                    <td className="px-5 py-2.5 text-ink-subtle">{m.vendor}</td>
                    <td className="px-5 py-2.5 font-mono text-[13px]">{m.model}</td>
                    <td className="px-5 py-2.5">{m.display_name ?? m.model}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-[13px] text-ink-subtle">
                      {m.context_window !== null ? `${(m.context_window / 1000).toFixed(0)}K` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-[13px] text-ink-subtle">
          模型列表实时从平台获取。使用 <code className="bg-butter px-1.5 py-0.5 rounded text-[13px]">GET /v1/models</code>（需认证）可以获取你当前渠道可用的模型。
        </p>
      </section>
    </article>
  );
}
